import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import ProductBrand from "../../models/product-brand";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { BsFillPlusCircleFill } from "react-icons/bs";
import ProductBrandUpdateModal from "./ProductBrandUpdateModal";
import ProductBrandDeleteModal from "./ProductBrandDeleteModal";
import { defaultValueOnUndefined } from "../../utils/fields";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  PRODUCT_BRANDS_CREATE,
  PRODUCT_BRANDS_UPDATE,
  PRODUCT_BRANDS_DESTROY,
} from "../../auth/permissions";

const ITEMS_PER_PAGE = 10;

const ProductBrandList = () => {
  const fields = [
    {
      key: "name",
      _classes: ["text-center"],
      label: "Nombre",
    },
    {
      key: "code",
      _classes: ["text-center"],
      label: "Código",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [search, setSearch] = useState<string>("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productBrand, setProductBrand] = useState<ProductBrand | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [productBrands, setProductBrands] = useState<ProductBrand[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchProductBrands = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const brandsStatus = await getList<ProductBrand>(
      "/product_brands/",
      limit,
      offset,
      urlParams
    );
    if (brandsStatus.status === SUCCESS) {
      if (brandsStatus.data !== undefined) {
        const count = brandsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setProductBrands(brandsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = brandsStatus.detail
        ? brandsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (brand: ProductBrand) => {
    setProductBrand(brand);
    setShowEditModal(true);
  };

  const onDeleteClick = (brand: ProductBrand) => {
    setProductBrand(brand);
    setShowDeleteModal(true);
  };

  const onProductBrandEditCancel = () => {
    setProductBrand(undefined);
    setShowEditModal(false);
  };

  const onProductBrandEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setProductBrand(undefined);
    setProductBrands([]);
    setLoading(true);
    fetchProductBrands();
  };

  const onProductBrandDeleteCancel = () => {
    setProductBrand(undefined);
    setShowDeleteModal(false);
  };

  const onProductBrandDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setProductBrand(undefined);
    setProductBrands([]);
    setLoading(true);
    fetchProductBrands();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchProductBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("productBrands")) {
        return;
      }
      fetchProductBrands();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Marcas</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent
                  permissionName={PRODUCT_BRANDS_CREATE}
                >
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={productBrands}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    code: (item: ProductBrand) => {
                      return (
                        <td className="text-center">
                          {defaultValueOnUndefined("-", item.code)}
                        </td>
                      );
                    },
                    actions: (item: ProductBrand) => {
                      const editButton = (
                        <PermissionRequiredComponent
                          permissionName={PRODUCT_BRANDS_UPDATE}
                        >
                          <CButton
                            className="text-white"
                            color="warning"
                            onClick={() => {
                              onUpdateClick(item);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </CButton>
                        </PermissionRequiredComponent>
                      );
                      const deleteButton = (
                        <PermissionRequiredComponent
                          permissionName={PRODUCT_BRANDS_DESTROY}
                        >
                          <CButton
                            className="text-white"
                            color="danger"
                            onClick={() => {
                              onDeleteClick(item);
                            }}
                          >
                            <i className="fa fa-trash"></i>
                          </CButton>
                        </PermissionRequiredComponent>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            {editButton}
                            {deleteButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <ProductBrandUpdateModal
        show={showEditModal}
        onCancel={onProductBrandEditCancel}
        onSuccess={onProductBrandEditSuccess}
        productBrand={productBrand}
      />
      <ProductBrandDeleteModal
        show={showDeleteModal}
        onCancel={onProductBrandDeleteCancel}
        onSuccess={onProductBrandDeleteSuccess}
        productBrand={productBrand}
      />
    </>
  );
};

export default ProductBrandList;
